import {
  ICertificationUseCase,
  IResourcesUseCase,
  ICurriculumUseCase,
  IAnalyticsUseCase
} from '../usecases';
import { CourseRepository } from '../repository';

/**
 * Catalog Service
 * Orchestrates catalog-related use cases for course and resource discovery
 * Handles search, recommendations, categories, and catalog analytics
 */
export class CatalogService {
  constructor(
    private certificationUseCase: ICertificationUseCase,
    private resourcesUseCase: IResourcesUseCase,
    private curriculumUseCase: ICurriculumUseCase,
    private analyticsUseCase: IAnalyticsUseCase,
    private courseRepository: CourseRepository
  ) {}

  /**
   * Unified catalog search across courses and resources
   */
  async searchCatalog(
    searchCriteria: {
      query?: string;
      type?: 'course' | 'resource' | 'all';
      category?: string;
      difficultyLevel?: string;
      priceMin?: number;
      priceMax?: number;
      instructor?: string;
      tags?: string[];
      rating?: number;
    },
    options: {
      sortBy?: 'relevance' | 'newest' | 'priceLow' | 'priceHigh' | 'rating' | 'popular';
      limit?: number;
      offset?: number;
    } = {},
    userId?: string,
    context?: any
  ): Promise<{
    results: {
      courses: any[];
      resources: any[];
    };
    stats: {
      totalCourses: number;
      totalResources: number;
      totalResults: number;
    };
    facets: {
      categories: Array<{ name: string; count: number; type: 'course' | 'resource' }>;
      difficultyLevels: Array<{ level: string; count: number }>;
      priceRanges: Array<{ range: string; count: number }>;
      instructors: Array<{ id: string; name: string; count: number }>;
    };
    recommendations: any[];
  }> {
    console.log('[CatalogService.searchCatalog] START - SearchCriteria:', searchCriteria, 'Options:', options, 'UserId:', userId);
    try {
      // Search courses and resources in parallel
      console.log('[CatalogService.searchCatalog] Searching courses and resources in parallel');
      const [courseResults, resourceResults] = await Promise.all([
        // Search courses (would call course repository)
        this.searchCourses(searchCriteria, options, userId, context),
        // Search resources
        this.resourcesUseCase.searchResources(
          {
            query: searchCriteria.query,
            contentType: searchCriteria.type === 'resource' ? undefined : undefined, // Would map properly
            difficultyLevel: searchCriteria.difficultyLevel,
            category: searchCriteria.category,
            instructor: searchCriteria.instructor,
            priceMin: searchCriteria.priceMin,
            priceMax: searchCriteria.priceMax,
            tags: searchCriteria.tags,
            isPublished: true
          },
          {
            sortBy: this.mapSortOption(options.sortBy),
            limit: Math.floor((options.limit || 20) / 2), // Split between courses and resources
            offset: options.offset
          },
          userId
        )
      ]);
      console.log('[CatalogService.searchCatalog] Search completed - Found', courseResults.length, 'courses and', resourceResults.resources.length, 'resources');

      // Mock course search results (would be implemented)
      const mockCourses = [
        {
          id: 'course1',
          title: 'Professional Dance Certification',
          instructor: 'Sarah Chen',
          price: 299.99,
          rating: 4.8,
          difficulty: 'intermediate',
          category: 'certification'
        }
      ];

      // Combine and sort results
      const allResults = {
        courses: mockCourses,
        resources: resourceResults.resources
      };

      // Calculate stats
      const stats = {
        totalCourses: mockCourses.length,
        totalResources: resourceResults.resources.length,
        totalResults: mockCourses.length + resourceResults.resources.length
      };

      // Aggregate facets
      const facets = {
        categories: [
          ...mockCourses.map(c => ({ name: c.category, count: 1, type: 'course' as const })),
          ...resourceResults.facets.categories.map(c => ({
            name: c.category || 'uncategorized',
            count: c.count,
            type: 'resource' as const
          }))
        ],
        difficultyLevels: [
          ...mockCourses.map(c => ({ level: c.difficulty, count: 1 })),
          ...resourceResults.facets.difficultyLevels.map(d => ({
            level: d.level || 'beginner',
            count: d.count
          }))
        ],
        priceRanges: resourceResults.facets.priceRanges, // Would also include course price ranges
        instructors: [
          { id: 'inst1', name: 'Sarah Chen', count: 5 },
          { id: 'inst2', name: 'Mike Johnson', count: 3 }
        ]
      };

      // Generate recommendations
      console.log('[CatalogService.searchCatalog] Generating recommendations for user:', userId);
      const recommendations = await this.generateRecommendations(
        userId,
        searchCriteria,
        allResults,
        context
      );
      console.log('[CatalogService.searchCatalog] Generated', recommendations.length, 'recommendations');

      // Track search analytics
      console.log('[CatalogService.searchCatalog] Tracking search analytics');
      await this.analyticsUseCase.trackUserActivity(
        userId || 'anonymous',
        {
          type: 'courseView', // Would be 'catalogSearch'
          metadata: {
            searchQuery: searchCriteria.query,
            searchType: searchCriteria.type,
            resultsCount: stats.totalResults,
            sortBy: options.sortBy
          }
        },
        context
      );
      console.log('[CatalogService.searchCatalog] Analytics tracking completed');

      console.log('[CatalogService.searchCatalog] SUCCESS - Total results:', stats.totalResults, 'recommendations:', recommendations.length);
      return {
        results: allResults,
        stats,
        facets,
        recommendations
      };
    } catch (error) {
      console.error('[CatalogService.searchCatalog] ERROR - Criteria:', JSON.stringify(searchCriteria), 'Error:', error);
      throw error;
    }
  }

  /**
   * Get featured/popular content for homepage
   */
  async getFeaturedContent(
    userId?: string,
    context?: any
  ): Promise<{
    featuredCourses: any[];
    featuredResources: any[];
    trendingTopics: Array<{ topic: string; growth: number }>;
    popularInstructors: Array<{ id: string; name: string; courses: number; rating: number }>;
    seasonalOffers: any[];
    personalizedRecommendations: any[];
  }> {
    console.log('[CatalogService.getFeaturedContent] START - UserId:', userId);
    try {
      // Mock featured content
      const featuredContent = {
        featuredCourses: [
          {
            id: 'courseFeatured1',
            title: 'Complete Ballet Mastery',
            instructor: 'Maria Rodriguez',
            price: 199.99,
            originalPrice: 299.99,
            rating: 4.9,
            students: 1250,
            badge: 'bestseller',
            image: '/images/courses/ballet-mastery.jpg'
          },
          {
            id: 'courseFeatured2',
            title: 'Contemporary Dance Fusion',
            instructor: 'Alex Chen',
            price: 149.99,
            rating: 4.7,
            students: 890,
            badge: 'trending',
            image: '/images/courses/contemporary-fusion.jpg'
          }
        ],
        featuredResources: [
          {
            id: 'resourceFeatured1',
            title: 'Dance Floor Maintenance Guide',
            type: 'document',
            price: 19.99,
            downloads: 450,
            rating: 4.5
          },
          {
            id: 'resourceFeatured2',
            title: 'Warm-up Routine Videos',
            type: 'video',
            price: 29.99,
            views: 1200,
            rating: 4.8
          }
        ],
        trendingTopics: [
          { topic: 'Ballet', growth: 45 },
          { topic: 'Contemporary', growth: 38 },
          { topic: 'Jazz', growth: 29 },
          { topic: 'Hip Hop', growth: 22 }
        ],
        popularInstructors: [
          {
            id: 'inst1',
            name: 'Maria Rodriguez',
            courses: 8,
            rating: 4.9,
            students: 3200
          },
          {
            id: 'inst2',
            name: 'Alex Chen',
            courses: 6,
            rating: 4.8,
            students: 2100
          }
        ],
        seasonalOffers: [
          {
            id: 'offer1',
            title: 'Spring Dance Special',
            description: '20% off all contemporary courses',
            discount: 20,
            validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            applicableCourses: ['contemporary', 'fusion']
          }
        ],
        personalizedRecommendations: userId ? [
          {
            id: 'rec1',
            title: 'Recommended: Advanced Turns',
            type: 'course',
            reason: 'Based on your ballet interest',
            matchScore: 92
          },
          {
            id: 'rec2',
            title: 'Recommended: Floor Care Guide',
            type: 'resource',
            reason: 'Complements your dance studies',
            matchScore: 78
          }
        ] : []
      };

      console.log('[CatalogService.getFeaturedContent] SUCCESS - Featured courses:', featuredContent.featuredCourses.length, 'resources:', featuredContent.featuredResources.length, 'personalized recs:', featuredContent.personalizedRecommendations.length);
      return featuredContent;
    } catch (error) {
      console.error('[CatalogService.getFeaturedContent] ERROR - UserId:', userId, 'Error:', error);
      throw error;
    }
  }

  /**
   * Get detailed content information
   */
  async getContentDetails(
    contentId: string,
    contentType: 'course' | 'resource',
    userId?: string,
    context?: any
  ): Promise<{
    content: any;
    creator: any;
    stats: {
      views: number;
      enrollments?: number;
      downloads?: number;
      rating: number;
      reviewCount: number;
    };
    relatedContent: any[];
    prerequisites: any[];
    learningObjectives: string[];
    curriculum?: any[];
    userProgress?: any;
    canAccess: boolean;
    accessReason?: string;
    pricing: {
      currentPrice: number;
      originalPrice?: number;
      discount?: number;
      currency: string;
      paymentOptions: string[];
    };
  }> {
    console.log('[CatalogService.getContentDetails] START - ContentId:', contentId, 'Type:', contentType, 'UserId:', userId);
    try {
      let content: any;
      let creator: any;
      let stats: any;
      let canAccess = true;
      let accessReason: string | undefined;
      let userProgress: any;

      if (contentType === 'course') {
        console.log('[CatalogService.getContentDetails] Processing course details');
        // Mock course details
        content = {
          id: contentId,
          title: 'Professional Ballet Certification',
          description: 'Comprehensive ballet training program',
          difficulty: 'intermediate',
          duration: '12 weeks',
          language: 'English',
          lastUpdated: new Date(),
          tags: ['ballet', 'certification', 'professional']
        };
        creator = {
          id: 'inst1',
          name: 'Maria Rodriguez',
          bio: 'Professional ballet dancer and instructor',
          rating: 4.9,
          courses: 8,
          students: 3200
        };
        stats = {
          views: 12500,
          enrollments: 450,
          rating: 4.8,
          reviewCount: 89
        };
        console.log('[CatalogService.getContentDetails] Course details loaded - Title:', content.title, 'Creator:', creator.name);

        if (userId) {
          console.log('[CatalogService.getContentDetails] Checking enrollment status for user:', userId);
          // Check enrollment status
          userProgress = {
            enrolled: true,
            progress: 65,
            completedLessons: 12,
            totalLessons: 20,
            lastAccessed: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
          };
          console.log('[CatalogService.getContentDetails] User progress found - Progress:', userProgress.progress + '%');
        }
      } else {
        console.log('[CatalogService.getContentDetails] Processing resource details');
        // Get resource details
        const resourceResult = await this.resourcesUseCase.getResource(
          contentId,
          userId
        );
        content = resourceResult.resource;
        creator = resourceResult.creator;
        canAccess = resourceResult.canAccess;
        accessReason = resourceResult.accessReason;
        console.log('[CatalogService.getContentDetails] Resource details loaded - CanAccess:', canAccess);

        stats = {
          views: 2500,
          downloads: 180,
          rating: 4.6,
          reviewCount: 24
        };
      }

      // Mock related content
      const relatedContent = [
        {
          id: 'related1',
          title: 'Ballet Fundamentals',
          type: 'course',
          price: 79.99,
          rating: 4.7
        },
        {
          id: 'related2',
          title: 'Point Work Techniques',
          type: 'resource',
          price: 24.99,
          rating: 4.5
        }
      ];

      // Mock prerequisites
      const prerequisites = [
        {
          id: 'prereq1',
          title: 'Basic Dance Knowledge',
          type: 'knowledge',
          required: true
        }
      ];

      const learningObjectives = [
        'Master fundamental ballet positions and movements',
        'Develop proper posture and alignment',
        'Learn basic choreography and performance techniques',
        'Understand ballet terminology and history'
      ];

      const curriculum = contentType === 'course' ? [
        {
          module: 'Foundation',
          lessons: [
            'Introduction to Ballet',
            'Basic Positions',
            'Arm and Hand Positions'
          ]
        },
        {
          module: 'Technique',
          lessons: [
            'Center Work',
            'Across the Floor',
            'Point Work Basics'
          ]
        }
      ] : undefined;

      const pricing = {
        currentPrice: content.price || 99.99,
        originalPrice: content.originalPrice,
        discount: content.discount,
        currency: 'USD',
        paymentOptions: ['creditCard', 'paypal', 'bankTransfer']
      };

      // Track view analytics
      console.log('[CatalogService.getContentDetails] Tracking content view analytics');
      await this.analyticsUseCase.trackUserActivity(
        userId || 'anonymous',
        {
          type: 'courseView', // Would be 'contentView'
          entityId: contentId,
          entityType: contentType,
          metadata: {
            contentTitle: content.title,
            contentType: contentType,
            canAccess: canAccess
          }
        },
        context
      );
      console.log('[CatalogService.getContentDetails] Analytics tracking completed');

      console.log('[CatalogService.getContentDetails] SUCCESS - ContentId:', contentId, 'Type:', contentType, 'CanAccess:', canAccess);
      return {
        content,
        creator,
        stats,
        relatedContent,
        prerequisites,
        learningObjectives,
        curriculum,
        userProgress,
        canAccess,
        accessReason,
        pricing
      };
    } catch (error) {
      console.error('[CatalogService.getContentDetails] ERROR - ContentId:', contentId, 'Type:', contentType, 'Error:', error);
      throw error;
    }
  }

  /**
   * Get category browsing experience
   */
  async browseCategory(
    categoryId: string,
    subfilters: {
      type?: 'course' | 'resource' | 'all';
      difficulty?: string;
      priceRange?: string;
      sortBy?: string;
    } = {},
    userId?: string,
    context?: any
  ): Promise<{
    category: {
      id: string;
      name: string;
      description: string;
      subcategories: Array<{ id: string; name: string; count: number }>;
      featuredContent: any[];
    };
    content: {
      courses: any[];
      resources: any[];
    };
    stats: {
      totalContent: number;
      totalStudents: number;
      averageRating: number;
    };
    breadcrumbs: Array<{ id: string; name: string }>;
    recommendations: any[];
  }> {
    console.log('[CatalogService.browseCategory] START - CategoryId:', categoryId, 'Subfilters:', subfilters, 'UserId:', userId);
    try {
      // Mock category data
      const categoryData = {
        category: {
          id: categoryId,
          name: 'Ballet',
          description: 'Comprehensive ballet training and certification programs',
          subcategories: [
            { id: 'sub1', name: 'Beginner Ballet', count: 15 },
            { id: 'sub2', name: 'Intermediate', count: 22 },
            { id: 'sub3', name: 'Advanced', count: 18 },
            { id: 'sub4', name: 'Professional', count: 8 }
          ],
          featuredContent: [
            {
              id: 'featured1',
              title: 'Complete Ballet Mastery',
              type: 'course',
              price: 199.99,
              rating: 4.9,
              students: 1250
            }
          ]
        },
        content: {
          courses: [
            {
              id: 'course1',
              title: 'Ballet Fundamentals',
              instructor: 'Maria Rodriguez',
              price: 79.99,
              rating: 4.7,
              duration: '6 weeks',
              difficulty: 'beginner'
            },
            {
              id: 'course2',
              title: 'Advanced Pointe Work',
              instructor: 'Sarah Chen',
              price: 149.99,
              rating: 4.8,
              duration: '8 weeks',
              difficulty: 'advanced'
            }
          ],
          resources: [
            {
              id: 'resource1',
              title: 'Ballet Warm-up Guide',
              type: 'video',
              price: 19.99,
              rating: 4.5,
              downloads: 320
            }
          ]
        },
        stats: {
          totalContent: 63,
          totalStudents: 15400,
          averageRating: 4.6
        },
        breadcrumbs: [
          { id: 'root', name: 'All Categories' },
          { id: categoryId, name: 'Ballet' }
        ],
        recommendations: userId ? [
          {
            id: 'rec1',
            title: 'Based on your interest in ballet',
            content: 'Contemporary Ballet Fusion',
            type: 'course',
            reason: 'Similar style, different approach'
          }
        ] : []
      };

      console.log('[CatalogService.browseCategory] SUCCESS - CategoryId:', categoryId, 'TotalContent:', categoryData.stats.totalContent, 'Recommendations:', categoryData.recommendations.length);
      return categoryData;
    } catch (error) {
      console.error('[CatalogService.browseCategory] ERROR - CategoryId:', categoryId, 'Error:', error);
      throw error;
    }
  }

  /**
   * Get personalized recommendations
   */
  async getPersonalizedRecommendations(
    userId: string,
    context?: any
  ): Promise<{
    becauseYouEnrolled: any[];
    trendingInYourInterests: any[];
    completeYourPath: any[];
    basedOnHistory: any[];
    seasonalPicks: any[];
  }> {
    console.log('[CatalogService.getPersonalizedRecommendations] START - UserId:', userId);
    try {
      // Mock personalized recommendations
      const recommendations = {
        becauseYouEnrolled: [
          {
            id: 'rec1',
            title: 'Advanced Pointe Work',
            type: 'course',
            reason: 'Students who enrolled in Ballet Fundamentals also enrolled in this',
            matchScore: 89
          }
        ],
        trendingInYourInterests: [
          {
            id: 'rec2',
            title: 'Contemporary Dance Techniques',
            type: 'course',
            reason: 'Trending in ballet and contemporary dance',
            trendScore: 85
          }
        ],
        completeYourPath: [
          {
            id: 'rec3',
            title: 'Professional Certification',
            type: 'course',
            reason: 'Next step in your learning journey',
            progressToNext: 65
          }
        ],
        basedOnHistory: [
          {
            id: 'rec4',
            title: 'Dance Floor Maintenance',
            type: 'resource',
            reason: 'Based on your completed courses',
            relevanceScore: 78
          }
        ],
        seasonalPicks: [
          {
            id: 'rec5',
            title: 'Spring Dance Collection',
            type: 'bundle',
            reason: 'Seasonal offer: 25% off dance bundles',
            discount: 25
          }
        ]
      };

      const totalRecs = Object.values(recommendations).reduce((sum, arr) => sum + arr.length, 0);
      console.log('[CatalogService.getPersonalizedRecommendations] SUCCESS - UserId:', userId, 'Total recommendations:', totalRecs);
      return recommendations;
    } catch (error) {
      console.error('[CatalogService.getPersonalizedRecommendations] ERROR - UserId:', userId, 'Error:', error);
      throw error;
    }
  }

  /**
   * Get all courses for admin management
   */
  async getAllCourses(
    filters?: {
      category?: string;
      difficulty?: string;
      instructor?: string;
      status?: string;
      search?: string;
      limit?: number;
      offset?: number;
    },
    context?: any
  ): Promise<{
    courses: any[];
    total: number;
    hasMore: boolean;
  }> {
    console.log('[CatalogService.getAllCourses] START - Filters:', filters);
    try {
      console.log('[CatalogService.getAllCourses] Calling courseRepository.getCourses');
      const courses = await this.courseRepository.getCourses(
        filters
          ? {
              instructorId: filters.instructor,
              difficultyLevel: filters.difficulty,
              categoryId: filters.category,
            }
          : undefined,
        filters?.limit
      );
      console.log('[CatalogService.getAllCourses] SUCCESS - Retrieved', courses.length, 'courses');

      return {
        courses,
        total: courses.length,
        hasMore: false // Simplified for now
      };
    } catch (error) {
      console.error('[CatalogService.getAllCourses] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get course by ID
   */
  async getCourse(
    courseId: string,
    context?: any
  ): Promise<any> {
    console.log('[CatalogService.getCourse] START - CourseId:', courseId);
    try {
      console.log('[CatalogService.getCourse] Calling courseRepository.getCourseById');
      const course = await this.courseRepository.getCourseById(courseId);
      console.log('[CatalogService.getCourse] SUCCESS - Course found:', course ? 'Yes' : 'No');
      return course;
    } catch (error) {
      console.error('[CatalogService.getCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Create a new course
   */
  async createCourse(
    courseData: any,
    context?: any
  ): Promise<any> {
    console.log('[CatalogService.createCourse] START - CourseData:', courseData);
    try {
      console.log('[CatalogService.createCourse] Calling courseRepository.createCourse');
      const course = await this.courseRepository.createCourse(courseData);
      console.log('[CatalogService.createCourse] Course created with ID:', course.id);

      // Track analytics
      console.log('[CatalogService.createCourse] Tracking course creation analytics');
      await this.analyticsUseCase.trackCourseEnrollment({
        userId: courseData.instructorId || 'system',
        courseId: course.id,
        enrollmentType: 'free'
      }, context);
      console.log('[CatalogService.createCourse] Analytics tracking completed');

      console.log('[CatalogService.createCourse] SUCCESS - Created course:', course.id);
      return course;
    } catch (error) {
      console.error('[CatalogService.createCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Update course
   */
  async updateCourse(
    courseId: string,
    updateData: any,
    context?: any
  ): Promise<any> {
    console.log('[CatalogService.updateCourse] START - CourseId:', courseId, 'UpdateData:', updateData);
    try {
      console.log('[CatalogService.updateCourse] Calling courseRepository.updateCourse');
      const course = await this.courseRepository.updateCourse(courseId, updateData);
      console.log('[CatalogService.updateCourse] SUCCESS - Updated course:', courseId);
      return course;
    } catch (error) {
      console.error('[CatalogService.updateCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Delete course
   */
  async deleteCourse(
    courseId: string,
    context?: any
  ): Promise<void> {
    console.log('[CatalogService.deleteCourse] START - CourseId:', courseId);
    try {
      console.log('[CatalogService.deleteCourse] Calling courseRepository.deleteCourse');
      await this.courseRepository.deleteCourse(courseId);
      console.log('[CatalogService.deleteCourse] SUCCESS - Deleted course:', courseId);
    } catch (error) {
      console.error('[CatalogService.deleteCourse] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get courses by instructor
   */
  async getCoursesByInstructor(
    instructorId: string,
    context?: any
  ): Promise<any[]> {
    console.log('[CatalogService.getCoursesByInstructor] START - InstructorId:', instructorId);
    try {
      console.log('[CatalogService.getCoursesByInstructor] Calling courseRepository.getCoursesByInstructor');
      const courses = await this.courseRepository.getCoursesByInstructor(instructorId, context);
      console.log('[CatalogService.getCoursesByInstructor] SUCCESS - Found', courses.length, 'courses for instructor:', instructorId);
      return courses;
    } catch (error) {
      console.error('[CatalogService.getCoursesByInstructor] ERROR:', error);
      throw error;
    }
  }

  // Helper methods

  private async searchCourses(
    criteria: any,
    options: any,
    userId?: string,
    context?: any
  ): Promise<any[]> {
    console.log('[CatalogService.searchCourses] START - Criteria:', criteria, 'Options:', options, 'UserId:', userId);
    // Mock course search - would implement actual search
    const results = [
      {
        id: 'course1',
        title: 'Professional Dance Certification',
        instructor: 'Sarah Chen',
        price: 299.99,
        rating: 4.8,
        difficulty: 'intermediate',
        category: 'certification'
      }
    ];
    console.log('[CatalogService.searchCourses] SUCCESS - Found', results.length, 'courses');
    return results;
  }

  private mapSortOption(sortBy?: string): any {
    console.log('[CatalogService.mapSortOption] Mapping sort option:', sortBy);
    const sortMapping: Record<string, any> = {
      relevance: undefined,
      newest: 'newest',
      priceLow: 'priceLow',
      priceHigh: 'priceHigh',
      rating: 'rating',
      popular: 'popular'
    };
    const mapped = sortMapping[sortBy || 'relevance'];
    console.log('[CatalogService.mapSortOption] Mapped to:', mapped);
    return mapped;
  }

  private async generateRecommendations(
    userId: string | undefined,
    criteria: any,
    results: any,
    context?: any
  ): Promise<any[]> {
    console.log('[CatalogService.generateRecommendations] START - UserId:', userId, 'HasResults:', !!(results.courses.length || results.resources.length));
    if (!userId) {
      const recs = [
        {
          type: 'popular',
          title: 'Most Popular This Week',
          content: results.courses[0] || results.resources[0]
        }
      ];
      console.log('[CatalogService.generateRecommendations] Generated', recs.length, 'anonymous recommendations');
      return recs;
    }

    const recs = [
      {
        type: 'personalized',
        title: 'Recommended for You',
        content: results.courses[0] || results.resources[0],
        reason: 'Based on your search history'
      }
    ];
    console.log('[CatalogService.generateRecommendations] Generated', recs.length, 'personalized recommendations');
    return recs;
  }
}
